import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";
import { useSearchParams } from "react-router-dom";
import _ from "lodash";
import moment from "moment";

import Selector from "./Selector";
import { useRacesContext } from "../../contexts/races";
import { useScheduleContext } from "../../contexts/schedule";
import { useStandingsContext } from "../../contexts/standings";
import { isTeamsEmpty } from "../../utils/helpers/standings";
import { getSeasonShortName } from "../../utils/helpers/season";
import theme from "../../styles/theme";

const SelectorContainer = styled.div`
  min-width: 150px;
  flex: 1;

  border-right: 1px solid ${theme.colors.darkLight};
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const SkeletonLine = styled.div`
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.06) 0%,
    rgba(255, 255, 255, 0.12) 50%,
    rgba(255, 255, 255, 0.06) 100%
  );
  background-size: 200% 100%;
  animation: ${shimmer} 1.2s ease-in-out infinite;
  height: 20px;
  width: ${props => props.$width || "80%"};
  margin: 14px 32px;
  border-radius: 4px;
`;

const SelectorSkeleton = () => (
  <div style={{ display: "flex", flexDirection: "column", paddingTop: 8 }}>
    <SkeletonLine $width="70%" />
    <SkeletonLine $width="50%" />
    <SkeletonLine $width="60%" />
  </div>
);

const Container = styled.div`
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
  width: 100%;
  justify-content: space-between;
  border-bottom: 1px solid ${theme.colors.darkLight};
  padding-bottom: 16px;
  margin-bottom: 16px;

  > ${SelectorContainer} {
    &:last-child {
      border-right: none;
      border-bottom: none;
    }
  }

  @media screen and (max-width: ${theme.breakpointsCustom.sm}px) {
    flex-direction: column;
    gap: 16px;

    > ${SelectorContainer} {
      & {
        border-right: none;
        border-bottom: 1px solid ${theme.colors.darkLight};
      }
    }
  }
`;
const TitleSelectorContainer = styled.div`
  text-align: center;
  text-transform: uppercase;
  font-size: 24px;
  font-weight: 400;
  font-family: "Montserrat", sans-serif;sans-serif; sans-serif;
`;

const RaceDate = styled.div`
  text-align: center;
  text-transform: uppercase;
  font-size: 16px;
  opacity: 0.4;
  font-weight: 400;
  font-family: "Montserrat", sans-serif;sans-serif; sans-serif;
`;

const RaceCar = styled.div`
  text-align: center;
  font-size: 16px;
  opacity: 0.4;
  font-weight: 400;
  font-family: "Montserrat", sans-serif;sans-serif; sans-serif;
`;

const SubTextContainer = styled.div`
  display: flex;
  justify-content: center;
  column-gap: 40px;
`;

const RaceTitleContainer = styled.div`
  margin: auto;
  display: flex;
  flex-direction: row;
  justify-content: center;

  @media screen and (max-width: ${theme.breakpointsCustom.sm}px) {
    flex-direction: column;
  }
`;

const RaceInfoContainer = styled.div`
  margin: auto;
  padding: 16px 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const DashElement = styled(TitleSelectorContainer)`
  &:after {
    content: "-";
  }

  margin: 0px 6px;

  @media screen and (max-width: ${theme.breakpointsCustom.sm}px) {
    display: none;
  }
`;

const simulatorOptions = ["iRacing", "EA F1", "Special Events"];

const defaultOptions = [
  { title: "Кваліфікація", key: "Qualification", id: 1 },
  { title: "Гонка", key: "Race", id: 3 },
];

const sprintOptions = [
  { title: "Кваліфікація", key: "Qualification", id: 1 },
  { title: "Спринт", key: "Sprint", id: 2 },
  { title: "Гонка", key: "Race", id: 3 },
];

const groupOptions = [
  { title: "Пілоти", key: "drivers", id: 1 },
  { title: "Команди", key: "teams", id: 2 },
];

const raceTypes = {
  Qualification: "qualiResults",
  Sprint: "sprintResults",
  Race: "raceResults",
};

const hasResultsByType = (race, type) => {
  return race && !!race[raceTypes[type]];
};

const RaceSelector = ({
  onRaceChange,
  onRaceTypeChange,
  view = "races",
  group = "drivers",
  onGroupChange,
  onSeasonChange,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { seasons, loadSchedule, loadSeasonsByType, loadSeasonsBySimulator } = useScheduleContext();
  const { races } = useRacesContext();
  const { standings } = useStandingsContext();

  const isStandings = view === "standings";

  const [selectedSimulator, setSelectedSimulator] = useState(
    searchParams.get("sim") || "iRacing",
  );
  const [selectedSeasonId, setSelectedSeasonId] = useState(null);
  const [selectedRaceId, setSelectedRaceId] = useState(null);
  const [selectedRaceType, setSelectedRaceType] = useState(
    defaultOptions[defaultOptions.length - 1],
  );

  const [loadingAll, setLoadingAll] = useState(true);

  const hasDetectedInitialSim = useRef(false);

  const [raceTypeOptions, setRaceTypeOptions] = useState([]);

  const seasonsByTab = useMemo(() => {
    const allSeasons = Object.values(seasons);
    const sortByDate = (list) => list.sort((a, b) => new Date(b.endDate) - new Date(a.endDate));
    return {
      "iRacing": sortByDate(allSeasons.filter(s => s.simulator === "iRacing" && (s.seasonType === "LEAGUE_SEASON" || !s.seasonType))),
      "EA F1": sortByDate(allSeasons.filter(s => s.simulator === "EA F1" && (s.seasonType === "LEAGUE_SEASON" || !s.seasonType))),
      "Special Events": sortByDate(allSeasons.filter(s => s.seasonType === "SPECIAL_EVENT"))
    };
  }, [seasons]);

  const simulatorRoots = useMemo(() => {
    return Object.values(seasons)
      .filter((s) => {
        const matchesSimulatorOrType = selectedSimulator === "Special Events"
          ? s.seasonType === "SPECIAL_EVENT"
          : s.simulator === selectedSimulator && (s.seasonType === "LEAGUE_SEASON" || !s.seasonType);

        if (selectedSimulator === "Special Events") return matchesSimulatorOrType;

        const matchesResults = s.races && s.races[0] ? s.races[0].hasResults : true;
        return matchesSimulatorOrType && matchesResults;
      })
      .filter((s) => !s.parentSeasonId)
      .sort((a, b) => new Date(b.endDate) - new Date(a.endDate));
  }, [seasons, selectedSimulator]);

  // The season this tab opens on: the newest one that has actually raced. `currentSeasonId` is the
  // wrong seed here -- a season is current from the moment it is created, so its first race has no
  // results yet and the page opens on an empty table. hasStandings comes from the backend because
  // the /seasons list carries no races, so the page cannot tell which seasons have raced on its own.
  const defaultSeasonIdForTab = useMemo(() => {
    const list = seasonsByTab[selectedSimulator] || [];
    const raced = list.find((season) => season.hasStandings) || list[0];
    if (!raced) return null;

    // A parent season holds no races of its own; its children do.
    const children = raced.childSeasons || [];
    if (children.length > 0) {
      return (children.find((child) => child.hasStandings) || children[0]).id;
    }
    return raced.id;
  }, [seasonsByTab, selectedSimulator]);

  const isFirstSeason = useMemo(() => {
    if (simulatorRoots.length === 0) return false;
    const firstParent = simulatorRoots[0];
    if (selectedSeasonId === firstParent.id) return true;
    if (firstParent.childSeasons && firstParent.childSeasons.length > 0) {
      return firstParent.childSeasons.some(child => child.id === selectedSeasonId);
    }
    return false;
  }, [simulatorRoots, selectedSeasonId]);

  const seasonRace = useMemo(() => {
    return seasons[selectedSeasonId]?.races.find((item) => item.id === selectedRaceId);
  }, [seasons, selectedSeasonId, selectedRaceId]);

  useEffect(() => {
    if (_.isNull(selectedSeasonId)) {
      const raceId = Number(searchParams.get("r"));
      const seasonId = Number(searchParams.get("s"));

      if (raceId && seasonId) {
        setSelectedRaceId(raceId);
        setSelectedSeasonId(seasonId);
        return;
      }

      // Wait for the season lists: the default depends on which seasons have raced.
      if (loadingAll) return;

      if (defaultSeasonIdForTab) {
        setSelectedSeasonId(defaultSeasonIdForTab);
      }
      return;
    }

    if (!seasons[selectedSeasonId]) return;

    const selectedRace = seasons[selectedSeasonId]?.races.find(
      (item) => item.id === selectedRaceId,
    );

    if (selectedRace) return;

    // The season the page opened on shows its latest round; a season the visitor picked themselves
    // starts at round one.
    if (isFirstSeason || selectedSeasonId === defaultSeasonIdForTab) {
      const lastFinishedRace = _.findLast(
        seasons[selectedSeasonId]?.races,
        (race) => race.hasResults,
      );

      if (lastFinishedRace) {
        setSelectedRaceId(lastFinishedRace.id);
      } else {
        setSelectedRaceId(seasons[selectedSeasonId]?.races[0]?.id);
      }
    } else {
      setSelectedRaceId(seasons[selectedSeasonId]?.races[0]?.id);
    }
    // eslint-disable-next-line
  }, [seasons, selectedSeasonId, isFirstSeason, defaultSeasonIdForTab, loadingAll]);

  useEffect(() => {
    const race = races[selectedRaceId];

    if (race) {
      onRaceChange(selectedRaceId);

      const options = race.sprintResults ? sprintOptions : defaultOptions;

      setRaceTypeOptions(options);
      setSelectedRaceType(options[options.length - 1]);
    } else if (selectedRaceId) {
      onRaceChange(selectedRaceId);
    }
  }, [races, selectedRaceId]); // eslint-disable-line

  // Sync selected simulator, season, and race IDs to search params in the URL
  useEffect(() => {
    if (selectedSimulator && selectedSeasonId && selectedRaceId) {
      const currentSim = searchParams.get("sim");
      const currentSeason = searchParams.get("s");
      const currentRace = searchParams.get("r");

      if (
        currentSim !== selectedSimulator ||
        Number(currentSeason) !== selectedSeasonId ||
        Number(currentRace) !== selectedRaceId
      ) {
        searchParams.set("r", String(selectedRaceId));
        searchParams.set("s", String(selectedSeasonId));
        searchParams.set("sim", selectedSimulator);
        setSearchParams(searchParams, { replace: true });
      }
    }
  }, [selectedSimulator, selectedSeasonId, selectedRaceId, searchParams, setSearchParams]);

  // Auto-detect simulator tab based on loaded season if not specified in URL or mismatched
  useEffect(() => {
    if (hasDetectedInitialSim.current) return;

    const initialSeasonId = Number(searchParams.get("s"));
    if (!initialSeasonId) {
      hasDetectedInitialSim.current = true;
      return;
    }

    const selectedSeason = seasons[initialSeasonId];
    if (selectedSeason) {
      const targetSim = selectedSeason.seasonType === "SPECIAL_EVENT"
        ? "Special Events"
        : selectedSeason.simulator;

      if (targetSim) {
        setSelectedSimulator(targetSim);
        hasDetectedInitialSim.current = true;
      }
    }
  }, [seasons, searchParams]);

  const round = useMemo(() => {
    if (!selectedRaceId || !seasons[selectedSeasonId]) return null;

    const seasonRaces = seasons[selectedSeasonId].races;
    return seasonRaces?.findIndex((item) => item.id === selectedRaceId) + 1;
  }, [seasons, selectedRaceId]); // eslint-disable-line

  useEffect(() => {
    onRaceTypeChange(selectedRaceType);
  }, [selectedRaceType]); // eslint-disable-line

  // The standings view is keyed by season, not by race, so the page needs the season id too
  useEffect(() => {
    if (selectedSeasonId && onSeasonChange) onSeasonChange(selectedSeasonId);
  }, [selectedSeasonId]); // eslint-disable-line

  // Prefetch all seasons on mount
  useEffect(() => {
    setLoadingAll(true);
    Promise.all([
      loadSeasonsBySimulator("iRacing"),
      loadSeasonsBySimulator("EA F1"),
      loadSeasonsByType("SPECIAL_EVENT")
    ]).finally(() => {
      setLoadingAll(false);
    });
  }, [loadSeasonsBySimulator, loadSeasonsByType]);

  // Auto-select another simulator if the current one has no seasons
  useEffect(() => {
    if (loadingAll) return;

    if (seasonsByTab[selectedSimulator]?.length === 0) {
      const firstEnabled = simulatorOptions.find(sim => seasonsByTab[sim]?.length > 0);
      if (firstEnabled) {
        setSelectedSimulator(firstEnabled);
      }
    }
  }, [loadingAll, seasonsByTab, selectedSimulator]);

  // Set selected season/race when selectedSimulator changes
  useEffect(() => {
    if (loadingAll) return;

    const list = seasonsByTab[selectedSimulator] || [];
    if (list.length > 0) {
      // Check if current selectedSeasonId is valid (either as a parent or as a child) for the selected simulator
      const currentValid = list.some(s => {
        if (s.id === selectedSeasonId) return true;
        if (s.childSeasons && s.childSeasons.length > 0) {
          return s.childSeasons.some(child => child.id === selectedSeasonId);
        }
        return false;
      });

      if (!currentValid && defaultSeasonIdForTab) {
        // Switching tabs lands on that tab's newest raced season, by the same rule as the initial
        // seed. The old per-tab branches read races[0].hasResults, which is unknown until that
        // season's schedule has been fetched -- hasStandings is known from the list alone.
        setSelectedSeasonId(defaultSeasonIdForTab);
      }
    }
  }, [selectedSimulator, loadingAll, seasonsByTab, selectedSeasonId, defaultSeasonIdForTab]);

  // When a season is selected, load its schedule (context guards against double-fetching)
  useEffect(() => {
    if (selectedSeasonId) {
      loadSchedule(selectedSeasonId);
    }
  }, [selectedSeasonId, loadSchedule]);

  const selectedSeason = seasons[selectedSeasonId];
  // Special events carry no team standings at all, so the option must not be selectable
  const teamsDisabled = isTeamsEmpty(standings[selectedSeasonId]?.teams);

  return (
    <Fragment>
      <Container>
        <SelectorContainer>
          <Selector
            selectedValue={selectedSimulator}
            onChange={(value) => setSelectedSimulator(value)}
            //values={Object.values(seasons)}
            values={simulatorOptions}
            isDisabled={(value) => !loadingAll && seasonsByTab[value]?.length === 0}
            getTitle={(value) => value}
          />
        </SelectorContainer>

        {selectedSeason ? (
          <Fragment>
            <SelectorContainer>
              <Selector
                selectedValue={selectedSeason}
                onChange={(value) => {
                  // Headers are not selectable
                  if (!value?.isHeader) setSelectedSeasonId(value.id);
                }}
                values={(() => {
                  const list = [];
                  simulatorRoots.forEach((parent) => {
                    const children = parent.childSeasons && parent.childSeasons.length > 0
                      ? parent.childSeasons
                          .map((child) => seasons[child.id] || child)
                          .filter(Boolean)
                      : [];

                    if (children.length > 0) {
                      // Parent with children: show as non-selectable group header
                      list.push({ ...parent, isHeader: true });
                      children.forEach((child) => {
                        list.push({ ...child, isChild: true });
                      });
                    } else {
                      // Parent without children: fully selectable
                      list.push(parent);
                    }
                  });
                  return list;
                })()}
                getTitle={(value) => value.shortName}
              />
            </SelectorContainer>
            {isStandings ? (
              <SelectorContainer>
                <Selector
                  selectedValue={groupOptions.find((option) => option.key === group)}
                  onChange={(value) => onGroupChange && onGroupChange(value.key)}
                  values={groupOptions}
                  getTitle={(value) => value.title}
                  isDisabled={(item) => item.key === "teams" && teamsDisabled}
                />
              </SelectorContainer>
            ) : (
              <Fragment>
                <SelectorContainer>
                  {selectedSeason.races && selectedSeason.races.length > 0 ? (
                    <Selector
                      selectedValue={selectedSeason.races.find(
                        (item) => item.id === selectedRaceId,
                      )}
                      onChange={(value) => setSelectedRaceId(value.id)}
                      values={selectedSeason.races}
                      isDisabled={(item) => !item.hasResults}
                      getTitle={(value, i) => `${i + 1}   ${value.shortName}`}
                    />
                  ) : (
                    <SelectorSkeleton />
                  )}
                </SelectorContainer>
                <SelectorContainer>
                  {selectedRaceId && raceTypeOptions.length > 0 ? (
                    <Selector
                      selectedValue={selectedRaceType}
                      onChange={setSelectedRaceType}
                      values={raceTypeOptions}
                      getTitle={(value) => value.title}
                      isDisabled={(item) => !hasResultsByType(races[selectedRaceId], item.key)}
                    />
                  ) : (
                    <SelectorSkeleton />
                  )}
                </SelectorContainer>
              </Fragment>
            )}
          </Fragment>
        ) : (
          <Fragment>
            <SelectorContainer>
              <SelectorSkeleton />
            </SelectorContainer>
            <SelectorContainer>
              <SelectorSkeleton />
            </SelectorContainer>
            {!isStandings && (
              <SelectorContainer>
                <SelectorSkeleton />
              </SelectorContainer>
            )}
          </Fragment>
        )}
      </Container>

      {selectedSeason && isStandings && (
        <RaceInfoContainer>
          <RaceTitleContainer>
            <TitleSelectorContainer>{getSeasonShortName(selectedSeason)}</TitleSelectorContainer>
            <DashElement />
            <TitleSelectorContainer>
              {groupOptions.find((option) => option.key === group)?.title}
            </TitleSelectorContainer>
          </RaceTitleContainer>
        </RaceInfoContainer>
      )}

      {selectedSeason && !isStandings && (
        <RaceInfoContainer>
          <RaceTitleContainer>
            <TitleSelectorContainer>{`${getSeasonShortName(selectedSeason)} Етап ${round}`}</TitleSelectorContainer>
            <DashElement />

            {seasonRace && (
              <TitleSelectorContainer>{`${seasonRace.shortName}  -  ${selectedRaceType.title}`}</TitleSelectorContainer>
            )}
          </RaceTitleContainer>
          <SubTextContainer>
            {seasonRace && <RaceCar>{`${seasonRace.car}`}</RaceCar>}
            {seasonRace && <RaceDate>{`${moment(seasonRace.date).format("DD/MM/YYYY")}`}</RaceDate>}
          </SubTextContainer>
        </RaceInfoContainer>
      )}
    </Fragment>
  );
};

export default RaceSelector;
